import './lib/schemaConfig';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@fontsource-variable/inter';
import '@fontsource-variable/jetbrains-mono';
import './design/globals.css';
import { App } from './app/App';
import { startOffline } from './features/offline/serviceWorker';
import { applyOfflineFlag } from './features/gaia/store';

// Cesium loads its workers and textures from this URL at runtime; the files are
// copied into public/cesium by scripts/copy-cesium.mjs (predev, prebuild, postinstall).
(window as unknown as { CESIUM_BASE_URL?: string }).CESIUM_BASE_URL = '/cesium/';

// ?gaia=offline (or online) on the first address sets the tab's GAIA mode before anything renders; the
// GAIA column is a lazy chunk, so this cannot wait for it.
applyOfflineFlag(window.location.search);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1 },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);

// Online/offline tracking always runs. The service worker registers in production
// builds (or dev with VITE_SW=1) and only from signed-in pages: its precache holds
// "/" and /data, which the Worker serves to a session only, so an install started
// from /login would fail. Sign-in reloads into the shell, which registers it. The
// phone tracker (/mobile) skips it: the precache (about 21 MB with Cesium and the
// models) is for the demo machine, not a phone on a cellular link.
const path = window.location.pathname;
startOffline({
  registerServiceWorker:
    (import.meta.env.PROD || import.meta.env.VITE_SW === '1') &&
    !path.startsWith('/login') &&
    !path.startsWith('/mobile'),
});
